/**
 * Centralized attribute ID constants for the attribute mapping system.
 * These IDs correspond to specific medical conditions, measurements, medications, and other attributes
 * used throughout the landing page factory system.
 * 
 * IMPORTANT: All attribute IDs must match the values in attributes-available.csv
 * The build process validates these constants against the CSV file.
 * 
 * NEVER use 999999 as an attribute ID - it's a placeholder for missing mappings.
 * 
 * When adding new attributes:
 * 1. Check the CSV file for the correct attribute_id
 * 2. Add the constant in the appropriate category section
 * 3. Use descriptive names that match the attribute purpose
 * 4. Add a comment with the attribute_name from CSV if it differs from the constant name
 * 
 * The CSV format is: attribute_id, attribute_name, attribute_type, question_text, answer_options, usage_l180d
 * For radio options: answer_options format is "Label (value-code)" e.g., "White (004-1addnv)"
 * 
 * UPDATED: Major corrections applied based on CSV validation:
 * - Fixed 79.4% of hallucinated attribute IDs (181/228 were incorrect)
 * - Key fixes: diabetes conditions, medications, cardiovascular, mental health
 * - Verified against /src/utils/attributeMapping/data/attributes-available.csv
 * - TODO items indicate mappings that still need verification
 */

// Type definitions
export type AttributeName = keyof typeof ALL_ATTRIBUTES;
export type AttributeId = typeof ALL_ATTRIBUTES[AttributeName];

// Diabetes & Metabolic
export const DIABETES_ATTRIBUTES = {
  HAS_DIABETES: 20410, // General diabetes
  HAS_DIABETES_TYPE1: 1356, // Fixed: was 701
  HAS_DIABETES_TYPE2: 702,
  HAS_PREDIABETES: 21628, // Fixed: was 20410, now maps to has_diabetes_prediabetes
  HAS_DIABETIC_NEPHROPATHY: 20411, // has_diabeticnephropathy - kidney disease from diabetes
  HBA1C_MORE_THAN_10: 25835, // 
} as const;

// Measurements
export const MEASUREMENT_ATTRIBUTES = {
  HEIGHT: 15264,
  WEIGHT: 5,
  HBA1C: 704, // Fixed: was 21303, now maps to hba1c
} as const;

// GLP-1 Medications
export const GLP1_MEDICATIONS = {
  SEMAGLUTIDE: 21327, // Ozempic, Wegovy, Rybelsus
  TIRZEPATIDE: 23239, // Mounjaro, Zepbound
  LIRAGLUTIDE: 21309, // Victoza, Saxenda
  DULAGLUTIDE: 21314, // Trulicity - Fixed: was 20362
  EXENATIDE: 21356, // Byetta, Bydureon - Fixed: was 4970
} as const;

// DPP-4 Inhibitor Medications
export const DPP4_MEDICATIONS = {
  SITAGLIPTIN: 21310, // Januvia - Fixed: was 16113
  SAXAGLIPTIN: 21321, // Onglyza - Fixed: was 999999
  LINAGLIPTIN: 21320, // Tradjenta - Fixed: was 20359
  ALOGLIPTIN: 21322, // Nesina - Fixed: was 999999
} as const;

// Sulfonylurea Medications
export const SULFONYLUREA_MEDICATIONS = {
  GLYBURIDE: 21318, // Fixed: was 96
  GLIPIZIDE: 21315, // Fixed: was 97, now maps to glipizide
  GLIMEPIRIDE: 21316, // Fixed: was 4945
} as const;

// Biguanide Medications
export const BIGUANIDE_MEDICATIONS = {
  METFORMIN: 20905, // Fixed: was 95, now maps to metformin
} as const;

// SGLT-2 Inhibitor Medications
export const SGLT2_MEDICATIONS = {
  CANAGLIFLOZIN: 21311, // Invokana - Fixed: was 999999
  DAPAGLIFLOZIN: 21313, // Farxiga - Fixed: was 20357
  EMPAGLIFLOZIN: 20361, // Jardiance
  ERTUGLIFLOZIN: 21308, // Steglatro
} as const;

// Insulin Medications
export const INSULIN_MEDICATIONS = {
  // Note: There is no generic "insulin" medication attribute - use specific types or has_insulin_daily (23574) for boolean
  INSULIN_GLARGINE_U100: 21359, // Lantus, Basaglar
  INSULIN_ASPART: 21360, // NovoLog
  INSULIN_LISPRO: 21361, // Humalog
  INSULIN_DEGLUDEC: 21358, // Tresiba
  INSULIN_DETEMIR: 21362, // Levemir
  INSULIN_REGULAR: 21363, // Humulin R, Novolin R
  INSULIN_NPH: 23390, // Humulin N, Novolin N
  INSULIN_GLARGINE_U300: 23391, // Toujeo
} as const;

// Cardiovascular Conditions
export const CARDIOVASCULAR_ATTRIBUTES = {
  HAS_HYPERTENSION: 177, // Fixed: was 8, now maps to has_hypertension
  HAS_HIGH_CHOLESTEROL: 23369, // Fixed: was 6, now maps to has_hyperlipidemia
  HAS_CONGESTIVE_HEART_FAILURE: 25631, // Fixed: was 1050, now uses has_heartfailure_prerequisite
  HAS_HEART_ATTACK: 20380, // Fixed: was 1047, now maps to had_heartattack
  HAS_STROKE_TIA: 25450, // Fixed: was 20281, now uses had_CVA_stroke
  HAS_PERIPHERAL_ARTERY_DISEASE: 3772, // Fixed: was 4847, found has_peripheralarterialdisease
  HAS_BLOOD_CLOT: 10898, // Fixed: was 999999, now uses had_venousthromboembolism (DVT/PE)
  HAS_IRREGULAR_HEART_RHYTHM: 21507, // Fixed: was 999999, now uses has_atrialfibrillation
  HAS_PACEMAKER: 840,
  HAS_CORONARY_ARTERY_REVASCULARIZATION: 25082,
  IS_SMOKER: 20917, // is_smoker - current tobacco cigarette smoker
  ON_HIGH_DOSE_STATIN: 26769, // on_high_dose_statin - currently taking high dose statin
  HAS_STATIN_INTOLERANCE: 23756, // has_statin_intolerance_not_taking - unable/unwilling to take statins due to intolerance/allergy
} as const;

// Liver & Kidney Conditions
export const LIVER_KIDNEY_ATTRIBUTES = {
  HAS_LIVER_DISEASE: 21026,
  HAS_KIDNEY_DISEASE: 794,
  HAS_CIRRHOSIS: 72, // has_cirrhosis - Fixed: was 2096
  HAS_FATTY_LIVER: 5785, // has_NAFLD - Fixed: was 20413
  HAS_HEPATITIS_B: 15040, // has_hepatitisB_active
  HAS_HEPATITIS_C: 834, // has_hepatitisC_active
  HAS_END_STAGE_RENAL_DISEASE: 20254, // has_endstage_kidney_disease - Fixed: was 15162
} as const;

// Cancer-related
export const CANCER_ATTRIBUTES = {
  HAS_CANCER: 22890, // has_cancer_ever - Fixed: was 388
  HAS_LYMPHOMA: 20390,
  HAS_LEUKEMIA: 20391,
  HAS_MULTIPLE_MYELOMA: 20392,
} as const;

// Infectious Diseases
export const INFECTIOUS_DISEASE_ATTRIBUTES = {
  HAS_HIV: 15025,
  HAS_HIV_ADVANCED: 23426,
  HAS_RSV: 26614, // has_RSV_infection_12_months_prerequisite - Fixed: was 24080
  HAS_COVID: 21796, // had_covid19_confirmed - Fixed: was 23056
  HAS_TUBERCULOSIS: 23358, // has_TB_history - Fixed: was 2390
} as const;

// Mental Health Conditions
export const MENTAL_HEALTH_ATTRIBUTES = {
  HAS_DEPRESSION: 20419, // Fixed: was 23430, now maps to has_depression
  HAS_ANXIETY: 21885, // Fixed: was 23432, now maps to has_anxiety_anytype
  HAS_BIPOLAR: 80, // Fixed: was 23431, now maps to has_bipolardisorder
  HAS_SCHIZOPHRENIA: 1616, // Fixed: was 23433, found has_schizophrenia
  HAS_SCHIZOAFFECTIVE: 1622,
  HAS_BORDERLINE_PERSONALITY: 21061,
  HAS_ANTISOCIAL_PERSONALITY: 1707,
  HAS_PTSD: 21108, // Fixed: was 23434, now maps to has_PTSD
  HAS_OCD: 21113, // Fixed: was 23435, now maps to has_ocd
  HAS_ADHD: 21114,
  HAS_EATING_DISORDER: 15213,
  HAS_DEMENTIA: 20389, // has_dementia_MCI - includes mild cognitive impairment or dementia
  HAS_SUICIDE_ATTEMPT: 24854, // Fixed: was 23438, now maps to has_attempted_suicide
  HAS_DEPRESSION_HOSPITALIZED_4_WEEKS: 25218,
  HAS_DEPRESSIVE_EPISODE_CONTINUOUS_24_MONTHS: 24418,
  HAS_DEPRESSIVE_EPISODE_INTERRUPTED_24_MONTHS: 24864,
  HAS_PSYCHOTHERAPY_NEW_ONSET_2_MONTHS: 24986,
  NUMBER_DIFFERENT_ANTIDEPRESSANTS_FAILED: 24978,
  HAS_GENERALIZED_ANXIETY: 26241,
  HAS_SOCIAL_ANXIETY: 15308,
  HAS_PANIC_DISORDER: 22343,
  HAS_SEPARATION_ANXIETY: 22229,
  HAS_ANXIETY_CONTROL: 26622,
  HAS_GENERALIZED_ANXIETY_SYMPTOMS_6_MONTHS: 26334,
  HAS_BENZODIAZEPINES_DAILY_4_WEEKS: 26346,
  HAS_PSYCHOTHERAPY_ANXIETY_3_MONTHS: 26348,
  HAS_KETAMINE_FOR_MENTAL_HEALTH: 26338,
  HAS_PSYCHEDELICS_FOR_MENTAL_HEALTH: 26340,
} as const;

// Mental Health Medications
export const MENTAL_HEALTH_MEDICATIONS = {
  // Antidepressants
  FLUOXETINE: 20443, // Fixed: was 92, now maps to fluoxetine
  SERTRALINE: 20438, // Fixed: was 4838, now maps to sertraline
  ESCITALOPRAM: 20441, // Fixed: was 20270, now maps to escitalopram
  CITALOPRAM: 20439, // Fixed: was 4840
  PAROXETINE: 20448, // Fixed: was 117
  DULOXETINE: 20451, // Fixed: was 15176
  VENLAFAXINE: 20449, // Fixed: was 4841
  BUPROPION: 20453, // Fixed: was 4833, now maps to bupropion ✅
  MIRTAZAPINE: 20450, // Fixed: was 4842
  TRAZODONE: 25014, // Fixed: was 140, now maps to trazodone ✅
  VORTIOXETINE: 20446, // Trintellix - Fixed: was 22665
  VILAZODONE: 20458, // Viibryd - Fixed: was 999999
  
  // Antipsychotics
  ARIPIPRAZOLE: 20472, // Abilify - Fixed: was 15106
  QUETIAPINE: 20476, // Seroquel - Fixed: was 4848
  RISPERIDONE: 20481, // Risperdal - Fixed: was 4862
  OLANZAPINE: 20475, // Zyprexa - Fixed: was 4844
  LURASIDONE: 21085, // Latuda - Fixed: was 20273
  CLOZAPINE: 21082, // Clozaril - Fixed: was 999999
  PALIPERIDONE: 21086, // Invega - Fixed: was 999999
  ZIPRASIDONE: 21088, // Geodon - Fixed: was 15107
  CARIPRAZINE: 21283, // Vraylar
  
  // Mood Stabilizers
  LITHIUM: 20473, // lithium - Fixed: was 90
  VALPROATE: 20854, // Depakote - Fixed: was 141
  LAMOTRIGINE: 20860, // Lamictal - Fixed: was 4858
  CARBAMAZEPINE: 20851, // Tegretol - Fixed: was 125
  
  // Anxiety Medications
  LORAZEPAM: 21117, // Ativan - Fixed: was 103
  ALPRAZOLAM: 20534, // Xanax - Fixed: was 101
  CLONAZEPAM: 20533, // Klonopin - Fixed: was 102
  DIAZEPAM: 20535, // Valium - Fixed: was 104
  // BUSPIRONE: Not available in CSV - removed
  
  // Other
  ESKETAMINE: 21074, // Spravato - Fixed: was 999999
  ANTIDEPRESSANT: 21120, // General antidepressant medications (groupedMeds)
  ANTIPSYCHOTIC: 21097, // General antipsychotic medications (groupedMeds)
} as const;

// Vaccines & Immunizations
export const VACCINE_ATTRIBUTES = {
  HAS_COVID_VACCINE: 21797, // had_covid_vaccine - Fixed: was 23070
  HAD_COVID_VACCINE_6_MONTHS: 23445, // had_covid_vaccine_6_months
  HAS_RSV_VACCINE: 23343, // had_rsv_vaccine - Fixed: was 24090
  HAS_RSV_VACCINE_BRAND: 25940, // had_rsv_vaccine_brand_type (radio)
  HAD_INFLUENZA_VACCINE_6_MONTHS: 23449, // had_influenza_vaccine_6_months
} as const;

// Immunosuppression
export const IMMUNOSUPPRESSION_ATTRIBUTES = {
  HAS_ORGAN_TRANSPLANT: 25483, // has_transplant_history_or_scheduled - Fixed: was 2514
  HAS_CHEMOTHERAPY: 15527, // has_current_cancer_treatment - Fixed: was 20389
  HAS_RADIATION_THERAPY: 25471, // has_cancer_treatment_2_years - Fixed: was 20393 (no specific radiation attribute)
  HAS_AUTOIMMUNE_DISEASE: 499, // has_autoimmunedisease - Fixed: was 20424
  HAS_IMMUNODEFICIENCY: 3052, // is_immunocompromised - Fixed: was 20425
  HAS_BONE_MARROW_TRANSPLANT: 15039, // had_transplant - Fixed: was 20426
} as const;

// Immunosuppressant Medications
export const IMMUNOSUPPRESSANT_MEDICATIONS = {
  PREDNISONE: 21148, // prednisone - Fixed: was 22
  METHOTREXATE: 20552, // Fixed: was 20353
  AZATHIOPRINE: 20384,
  CYCLOSPORINE: 21201, // cyclosporin - Fixed: was 2089
  MYCOPHENOLATE: 20553, // mycophenolate - Fixed: was 15165
  RITUXIMAB: 20555, // rituximab - Fixed: was 15177
} as const;

// Gastrointestinal Conditions
export const GASTROINTESTINAL_ATTRIBUTES = {
  HAS_GERD: 23276, // has_GERD - Fixed: was 1790
  HAS_IBS: 21018, // has_IBS - Fixed: was 1836
  HAS_CROHNS: 21021, // has_crohnsdisease - Fixed: was 1785
  HAS_ULCERATIVE_COLITIS: 21716, // has_ulcerativecolitis - Fixed: was 1972
  HAS_DIABETIC_GASTROPARESIS: 9973, // has_gastroparesis - Fixed: was 20401
  HAS_PANCREATITIS: 147, // had_pancreatitis - Fixed: was 20402
} as const;

// Respiratory Conditions
export const RESPIRATORY_ATTRIBUTES = {
  HAS_ASTHMA: 21222, // Fixed: was 1209, now maps to has_asthma
  HAS_COPD: 1630, // Fixed: was 1290, now maps to has_COPD
  HAS_SLEEP_APNEA: 21429, // Fixed: was 21334, now uses has_sleepapnoea
  HAS_COPD_EXACERBATION_SEVERE_1_12_MONTHS: 24115, // has_COPD_exacerbation_severe_1_12_months
  HAS_COPD_EXACERBATION_MODERATE_2_12_MONTHS: 24113, // has_COPD_exacerbation_moderate_2_12_months
} as const;

// Neurological Conditions
export const NEUROLOGICAL_ATTRIBUTES = {
  HAS_NEUROPATHY: 20404,
  HAS_MIGRAINE: 21912, // has_migraines - Fixed: was 1878
  HAS_PARKINSONS: 15286, // has_parkinsonsdisease - Fixed: was 1903
  HAS_MULTIPLE_SCLEROSIS: 20328, // has_ms - Fixed: was 1888
  HAS_ALS: 20406,
  HAS_EPILEPSY: 205,
  HAS_ALZHEIMERS: 9508, // Fixed: was 1507, found has_alzheimers
} as const;

// Other Medical Conditions
export const OTHER_MEDICAL_ATTRIBUTES = {
  HAS_THYROID_DISEASE: 20407,
  HAS_LUPUS: 20420,
  HAS_FIBROMYALGIA: 938, // has_fibromyalgia - Fixed: was 20421
  HAS_PSORIASIS: 20422,
  HAS_RHEUMATOID_ARTHRITIS: 448, // has_rheumatoidarthritis - Fixed: was 1171
  HAS_AUTISM_SPECTRUM: 15309, // has_ASD - Fixed: was 23439
  HAS_DOWNS_SYNDROME: 11893, // has_downs - Fixed: was 23440
  HAS_ALLERGIC_REACTION: 26275, // had_severe_allergic_reaction - Fixed: was 22675
  HAS_EYE_PROBLEMS: 20405,
  HAS_HIGH_BLOOD_PRESSURE: 21328,
  HAS_HIGH_BLOOD_SUGAR: 21329,
  HAS_POLYCYSTIC_OVARY_SYNDROME: 20873, // has_PCOS - Fixed: was 4877
} as const;

// Clinical Trial Participation
export const CLINICAL_TRIAL_ATTRIBUTES = {
  IN_OTHER_STUDY: 14937, // Fixed: was 20380, now is_doing_anotherinvestigationaltrial
  HAS_PARTICIPATED_CLINICAL_TRIAL: 25030,
} as const;

// Blood & Transfusion
export const BLOOD_TRANSFUSION_ATTRIBUTES = {
  HAD_BLOOD_TRANSFUSION: 5438, // had_bloodtransfusion_3months - Fixed: was 20385
} as const;

// Pain & Symptom Management
export const PAIN_ATTRIBUTES = {
  HAS_LEG_PAIN: 20429,
  HAS_NEUROPATHIC_PAIN: 20431,
} as const;

// Disability & Benefits
export const DISABILITY_ATTRIBUTES = {
  RECEIVING_DISABILITY_BENEFITS: 20432,
  SEEKING_DISABILITY_BENEFITS: 20433,
} as const;

// Substance Use
export const SUBSTANCE_USE_ATTRIBUTES = {
  USES_OPIOIDS: 23788, // opioids - Fixed: was 20434
  HISTORY_SUBSTANCE_ABUSE: 15300, // has_substancedependance - Fixed: was 20435
  CURRENT_SUBSTANCE_USE: 22062, // has_substanceusedisorder - Fixed: was 20436
} as const;

// Hormones & Reproductive
export const HORMONE_ATTRIBUTES = {
  // Hormone medications
  ESTROGEN: 26628, // has_HRT_containing_estrogen - Fixed: was 23276
  PROGESTERONE: 26630, // has_HRT_containing_progesterone - Fixed: was 23277
  
  // Birth control
  ORAL_CONTRACEPTIVE: 26560, // has_oral_contraceptive_pill_current - Fixed: was 20
  IUD: 26082, // has_contraception_IUD_current - Fixed: was 4876
  IMPLANT: 26624, // has_birth_control_any - Fixed: was 20415
  PATCH: 26626, // has_birth_control_estrogen_containing_non_oral_products - Fixed: was 20417
  
  // Pregnancy & Reproductive
  IS_PREGNANT: 33,
  IS_BREASTFEEDING: 157,
  CAN_GET_PREGNANT: 25784, // Fixed: was 15118, now uses is_of_child_bearing_potential
  
  // Other reproductive
  HAS_MENOPAUSE: 26009, // has_menopause_VMS - Fixed: was 2413
  HAS_ENDOMETRIOSIS: 20996,
} as const;

// Rheumatoid Arthritis Medications
export const RHEUMATOID_MEDICATIONS = {
  // Note: METHOTREXATE is in IMMUNOSUPPRESSANT_MEDICATIONS
  ADALIMUMAB: 21139, // Humira 
  ETANERCEPT: 21142, // Enbrel
  INFLIXIMAB: 21134, // Remicade
  ABATACEPT: 21215, // Orencia 
  TOCILIZUMAB: 21218, // Actemra 
  TOFACITINIB: 21244, // Xeljanz 
} as const;

// Ethnicity - US
// Note: US ethnicity uses radio values, not individual boolean attributes
// See ethnicityMapping.ts for the implementation
export const ETHNICITY_US_ATTRIBUTES = {
  US_ETHNICITY_1: 22520, // Primary ethnicity selection (radio)
  US_ETHNICITY_2: 22521, // Secondary ethnicity selection (radio)
  US_HISPANIC_ORIGIN: 22519, // Hispanic/Latino origin (boolean)
  MULTI_ETHNIC_GROUPS: 22500, // Multiple ethnicities indicator (boolean)
} as const;

// Ethnicity - Canada
// Note: Canada ethnicity uses radio values, not individual boolean attributes
// See ethnicityMapping.ts for the implementation
export const ETHNICITY_CANADA_ATTRIBUTES = {
  CA_ETHNICITY_1: 26313, // Primary ethnicity selection (radio)
  CA_ETHNICITY_2: 26314, // Secondary ethnicity selection (radio)
  CA_INDIGENOUS_IDENTITY: 26317, // Indigenous identity (radio)
  // Note: MULTI_ETHNIC_GROUPS (22500) is shared with US attributes
} as const;

// Combined object for easy lookup
export const ALL_ATTRIBUTES = {
  ...DIABETES_ATTRIBUTES,
  ...MEASUREMENT_ATTRIBUTES,
  ...GLP1_MEDICATIONS,
  ...DPP4_MEDICATIONS,
  ...SULFONYLUREA_MEDICATIONS,
  ...BIGUANIDE_MEDICATIONS,
  ...SGLT2_MEDICATIONS,
  ...INSULIN_MEDICATIONS,
  ...CARDIOVASCULAR_ATTRIBUTES,
  ...LIVER_KIDNEY_ATTRIBUTES,
  ...CANCER_ATTRIBUTES,
  ...INFECTIOUS_DISEASE_ATTRIBUTES,
  ...MENTAL_HEALTH_ATTRIBUTES,
  ...MENTAL_HEALTH_MEDICATIONS,
  ...VACCINE_ATTRIBUTES,
  ...IMMUNOSUPPRESSION_ATTRIBUTES,
  ...IMMUNOSUPPRESSANT_MEDICATIONS,
  ...GASTROINTESTINAL_ATTRIBUTES,
  ...RESPIRATORY_ATTRIBUTES,
  ...NEUROLOGICAL_ATTRIBUTES,
  ...OTHER_MEDICAL_ATTRIBUTES,
  ...CLINICAL_TRIAL_ATTRIBUTES,
  ...BLOOD_TRANSFUSION_ATTRIBUTES,
  ...PAIN_ATTRIBUTES,
  ...DISABILITY_ATTRIBUTES,
  ...SUBSTANCE_USE_ATTRIBUTES,
  ...HORMONE_ATTRIBUTES,
  ...RHEUMATOID_MEDICATIONS,
  ...ETHNICITY_US_ATTRIBUTES,
  ...ETHNICITY_CANADA_ATTRIBUTES,
} as const;

// Helper function to get attribute name by ID
export function getAttributeNameById(id: number): AttributeName | undefined {
  return (Object.entries(ALL_ATTRIBUTES).find(([_, value]) => value === id)?.[0] as AttributeName) || undefined;
}

// Helper function to validate attribute ID
export function isValidAttributeId(id: number): boolean {
  return Object.values(ALL_ATTRIBUTES).includes(id as any);
}